import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { analytics } from 'analytics';

import { AUTOSAVE_DEBOUNCE_MS } from '../../constants';
import { useApiContext } from '../../contexts/apiContext';
import { SankeyAutosaveContext } from '../../contexts/sankeyAutosaveContext';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { formToSankey, useSankeySyncer } from '../../utils/form';
import { useCurrentRouteSankeyId } from '../../utils/routes';
import { useCurrentUser } from '../../utils/user';

type Props = {
  children: React.ReactNode;
};

/*
 * This hook is used to for autosaving Sankey diagrams based on their form data.
 * The autosave is triggered by any change to the form data and it's debounced.
 * Since we keep the form data in sync with Django inputs as well, the autosave
 * will make sure to pull all the latest changes across the app (React + Django).
 */
const SankeyAutosaveProvider: React.FC<Props> = ({ children }) => {
  const { api } = useApiContext();
  const { form } = useSankeyFormContext();
  const { user } = useCurrentUser();
  const currentSankeyId = useCurrentRouteSankeyId();

  const [lastSaveTimestamp, setLastSaveTimestamp] = useState<string>();
  const [lastSaveError, setLastSaveError] = useState<string>();

  // Reset autosave state when the current Sankey changes
  // TODO: Get rid of this once we know how to get the timestamp from the Sankey itself
  useEffect(() => {
    setLastSaveTimestamp(undefined);
    setLastSaveError(undefined);
  }, [currentSankeyId]);

  const { mutateAsync: patchSankey, isLoading: isSaving } = useMutation(
    async () => {
      try {
        await api().sankeys.partialUpdate({
          id: currentSankeyId,
          patchedSankeyRequest: formToSankey(form),
        });
        // If the form is valid and the API call succeeds, update the
        // timestamp and clear any previous error messages.
        setLastSaveTimestamp(dayjs().toISOString());
        setLastSaveError(undefined);
      } catch (e: any) {
        // If the form is valid but the API call fails, show verbose
        // response to the user and report the error to Sentry.
        const statusCode = e?.response?.status ?? 0;
        setLastSaveError(
          e?.response?.data && statusCode >= 400 && statusCode < 500
            ? JSON.stringify(e.response.data)
            : 'An unexpected error occurred, please try reloading the page.'
        );
        analytics.reportError('Diagram autosave error', { error: e }, false);
      }
    }
  );

  const saveSankey = form.handleSubmit(
    () => patchSankey(),
    (errors) => {
      // If the form has been changed and is invalid, show an error message to the user.
      // TODO: Prevent this case altogether by validating individual input elements and
      // keeping the form valid at all times.
      setLastSaveError('Please fix the highlighted errors');
      analytics.reportError(
        'Diagram form validation error',
        { errors: JSON.stringify(errors) },
        false
      );
    }
  );

  useSankeySyncer({
    callback: saveSankey,
    debounceMs: AUTOSAVE_DEBOUNCE_MS,
    isEnabled: !!user?.can_update_sankeys,
  });

  return (
    <SankeyAutosaveContext.Provider
      value={{ isSaving, lastSaveTimestamp, lastSaveError }}
    >
      {children}
    </SankeyAutosaveContext.Provider>
  );
};

export default SankeyAutosaveProvider;
